import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import InmueblesPanel from './InmueblesPanel';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Landmark, Loader2, TrendingUp, TrendingDown, FileText, Save, Info, FileSpreadsheet, Download } from 'lucide-react';
import { fiscalidadAPI } from '../api';
import { toast } from '../hooks/use-toast';
import BienesInversion from './BienesInversion';
import LiquidacionesTrimestrales from './LiquidacionesTrimestrales';
import Modelo349 from './Modelo349';
import JustificantesFiscales from './JustificantesFiscales';
import AvisosFiscales from './AvisosFiscales';

const eur = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', useGrouping: true, minimumFractionDigits: 2 }).format(Number(n || 0));
const ANIOS = [2024, 2025, 2026, 2027];

const Casilla = ({ codigo, label, valor, bold, negativo }) => (
  <div className={`flex items-center justify-between py-2 px-3 rounded ${bold ? 'bg-slate-100 font-semibold' : ''}`}>
    <span className="text-sm text-slate-700">
      {codigo && <span className="inline-block w-16 text-xs text-slate-400 font-mono">[{codigo}]</span>}
      {label}
    </span>
    <span className={`text-sm tabular-nums ${negativo && valor < 0 ? 'text-red-600' : 'text-slate-900'}`}>{eur(valor)}</span>
  </div>
);

const ResultadoBanner = ({ resultado, aIngresar, aCompensar, negativo }) => {
  const positivo = (aIngresar || 0) > 0;
  return (
    <div className={`rounded-lg p-4 flex items-center justify-between ${positivo ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`} data-testid="fiscal-resultado">
      <div className="flex items-center gap-3">
        {positivo ? <TrendingUp className="w-6 h-6 text-red-600" /> : <TrendingDown className="w-6 h-6 text-green-600" />}
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">{positivo ? 'A ingresar' : (aCompensar || negativo ? 'A compensar / negativo' : 'Resultado')}</p>
          <p className={`text-2xl font-bold ${positivo ? 'text-red-700' : 'text-green-700'}`}>
            {eur(positivo ? aIngresar : (aCompensar || negativo || 0))}
          </p>
        </div>
      </div>
      <span className="text-xs text-slate-400">Resultado liquidación: {eur(resultado)}</span>
    </div>
  );
};

const FiscalidadTab = () => {
  const [ejercicio, setEjercicio] = useState(2026);
  const [tab, setTab] = useState('dashboard');
  const [trimestre, setTrimestre] = useState(1);
  const [loading, setLoading] = useState(false);
  const [resumen, setResumen] = useState(null);
  const [iva, setIva] = useState(null);
  const [irpf, setIrpf] = useState(null);
  const [config, setConfig] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);
  const [m390, setM390] = useState(null);
  const [m100, setM100] = useState(null);
  const [dp, setDp] = useState({ edad65: false, edad75: false, hijos: 0, hijos_menor3: 0, discapacidad: 0 });
  const [ret190, setRet190] = useState(null);
  const [exportTrim, setExportTrim] = useState('0');
  const [exportFormato, setExportFormato] = useState('completo');
  const [exporting, setExporting] = useState(false);
  const [exportingZip, setExportingZip] = useState(false);

  const loadResumen = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        fiscalidadAPI.getResumen(ejercicio),
        fiscalidadAPI.getConfig(),
      ]);
      setResumen(r.data);
      setConfig(c.data);
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo cargar el resumen fiscal', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [ejercicio]);

  const loadDeclaracion = useCallback(async () => {
    try {
      const [i, j] = await Promise.all([
        fiscalidadAPI.getIVA(ejercicio, trimestre),
        fiscalidadAPI.getIRPF(ejercicio, trimestre),
      ]);
      setIva(i.data);
      setIrpf(j.data);
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo calcular la declaración', variant: 'destructive' });
    }
  }, [ejercicio, trimestre]);

  useEffect(() => { loadResumen(); }, [loadResumen]);
  useEffect(() => { loadDeclaracion(); }, [loadDeclaracion]);
  useEffect(() => { fiscalidadAPI.getModelo390(ejercicio).then(r => setM390(r.data)).catch(() => setM390(null)); }, [ejercicio]);
  useEffect(() => { fiscalidadAPI.getModelo190(ejercicio).then(r => setRet190(r.data)).catch(() => setRet190(null)); }, [ejercicio]);
  useEffect(() => { fiscalidadAPI.getModelo100(ejercicio, { edad65: dp.edad65, edad75: dp.edad75, hijos: dp.hijos, hijos_menor3: dp.hijos_menor3, discapacidad: dp.discapacidad }).then(r => setM100(r.data)).catch(() => setM100(null)); }, [ejercicio, dp]);

  const descargarLibrosAEAT = async () => {
    try {
      const r = await fiscalidadAPI.librosRegistroAEAT(ejercicio);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `libros_registro_aeat_${ejercicio}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast({ title: 'Libros Registro AEAT descargados' });
    } catch (e) { toast({ title: 'Error', description: 'No se pudieron generar los libros', variant: 'destructive' }); }
  };

  const descargarGestoria = async () => {
    setExporting(true);
    try {
      const r = await fiscalidadAPI.exportGestoria(ejercicio, { trimestre: parseInt(exportTrim), formato: exportFormato });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      const periodo = exportTrim === '0' ? 'Anual' : `${exportTrim}T`;
      a.href = url; a.download = `gestoria_${ejercicio}_${periodo}_${exportFormato}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast({ title: 'Libro para gestoría descargado' });
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo generar el Excel', variant: 'destructive' });
    } finally { setExporting(false); }
  };

  const descargarGestoriaZip = async () => {
    setExportingZip(true);
    const periodo = exportTrim === '0' ? 'Anual' : `${exportTrim}T`;
    toast({
      title: 'Preparando el informe completo',
      description: `Se están generando los PDF de ${periodo} ${ejercicio}. Puede tardar un par de minutos; no cierres la pestaña.`
    });
    try {
      const r = await fiscalidadAPI.exportGestoriaZip(ejercicio, { trimestre: parseInt(exportTrim), formato: exportFormato });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url; a.download = `gestoria_${ejercicio}_${periodo}.zip`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast({
        title: 'Informe completo descargado',
        description: 'Dentro está el Excel, las facturas emitidas, las facturas de gastos y un LEEME.txt con el índice y lo que falte.'
      });
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo generar el informe completo', variant: 'destructive' });
    } finally { setExportingZip(false); }
  };

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      const { data } = await fiscalidadAPI.updateConfig(config);
      setConfig(data);
      toast({ title: 'Configuración fiscal guardada' });
      loadResumen(); loadDeclaracion();
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo guardar', variant: 'destructive' });
    } finally {
      setSavingCfg(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="fiscalidad-tab">
      {/* Cabecera: ejercicio + titular */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Landmark className="w-6 h-6 text-indigo-600" />
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Fiscalidad</h2>
            <p className="text-xs text-slate-500">{config?.titular_nombre} · {config?.titular_nif} · IAE {config?.epigrafe_iae} · Régimen {config?.regimen_irpf?.toUpperCase()}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-slate-500">Ejercicio</Label>
          <Select value={String(ejercicio)} onValueChange={(v) => setEjercicio(Number(v))}>
            <SelectTrigger className="w-28" data-testid="fiscal-ejercicio"><SelectValue /></SelectTrigger>
            <SelectContent>{ANIOS.map(a => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {loading && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto gap-1 justify-start">
          <TabsTrigger value="dashboard" data-testid="fiscal-subtab-dashboard">Resumen</TabsTrigger>
          <TabsTrigger value="iva" data-testid="fiscal-subtab-iva">IVA (303)</TabsTrigger>
          <TabsTrigger value="irpf" data-testid="fiscal-subtab-irpf">IRPF (130)</TabsTrigger>
          <TabsTrigger value="retenciones" data-testid="fiscal-subtab-retenciones">Retenciones (111)</TabsTrigger>
          <TabsTrigger value="liquidaciones" data-testid="fiscal-subtab-liquidaciones">Liquidaciones</TabsTrigger>
          <TabsTrigger value="intracomunitarias" data-testid="fiscal-subtab-349">Intracomunitarias (349)</TabsTrigger>
          <TabsTrigger value="inmuebles" data-testid="fiscal-subtab-inmuebles">Inmuebles (alquiler)</TabsTrigger>
          <TabsTrigger value="justificantes" data-testid="fiscal-subtab-justificantes">Justificantes AEAT (PDF)</TabsTrigger>
          <TabsTrigger value="cierre" data-testid="fiscal-subtab-cierre">Cierre anual</TabsTrigger>
          <TabsTrigger value="bienes" data-testid="fiscal-subtab-bienes">Bienes inversión</TabsTrigger>
          <TabsTrigger value="config" data-testid="fiscal-subtab-config">Configuración</TabsTrigger>
        </TabsList>

        {/* DASHBOARD */}
        <TabsContent value="dashboard" className="mt-4 space-y-4">
          <AvisosFiscales ejercicio={ejercicio} />
          <Card className="border-indigo-200 bg-indigo-50/40" data-testid="fiscal-acceso-justificantes">
            <CardContent className="pt-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-slate-800">Justificantes de las presentaciones</p>
                <p className="text-xs text-slate-600">
                  Guarda aquí el PDF de cada modelo que presentes en la Agencia Tributaria (303, 130,
                  111, 190, 390, 349...) y recupéralo cuando lo necesites.
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => setTab('justificantes')}
                      data-testid="fiscal-ir-justificantes">
                Subir o ver justificantes
              </Button>
            </CardContent>
          </Card>
          {resumen?.arrendamientos?.facturas > 0 && (
            <Card className="border-emerald-200 bg-emerald-50/40" data-testid="fiscal-arrendamientos">
              <CardHeader>
                <CardTitle className="text-sm">
                  Arrendamiento de inmuebles {ejercicio} · capital inmobiliario
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div><p className="text-xs text-slate-500">Ingresos por alquiler</p>
                    <p className="text-lg font-bold text-slate-900"
                       data-testid="fiscal-arr-ingresos">{eur(resumen.arrendamientos.ingresos)}</p></div>
                  <div><p className="text-xs text-slate-500">IVA repercutido</p>
                    <p className="text-lg font-bold text-slate-900">{eur(resumen.arrendamientos.iva_repercutido)}</p></div>
                  <div><p className="text-xs text-slate-500">Retenciones 19 % soportadas</p>
                    <p className="text-lg font-bold text-emerald-700">{eur(resumen.arrendamientos.retenciones_soportadas)}</p></div>
                  <div><p className="text-xs text-slate-500">Gastos imputados</p>
                    <p className="text-lg font-bold text-slate-900">{eur(resumen.arrendamientos.gastos)}</p></div>
                </div>
                <div className="space-y-1">
                  {resumen.arrendamientos.trimestres.filter(t => t.facturas > 0).map(t => (
                    <div key={t.trimestre} className="flex justify-between text-xs text-slate-600"
                         data-testid={`fiscal-arr-trimestre-${t.trimestre}`}>
                      <span>{t.trimestre}º trimestre · {t.facturas} factura(s)</span>
                      <span>{eur(t.ingresos)} + IVA {eur(t.iva_repercutido)} · retenido {eur(t.retenciones_soportadas)}</span>
                    </div>
                  ))}
                </div>
                {resumen.arrendamientos.arrendatarios.length > 0 && (
                  <div className="space-y-1 border-t pt-2">
                    <p className="text-xs font-medium text-slate-600">Por arrendatario</p>
                    {resumen.arrendamientos.arrendatarios.map((a, i) => (
                      <div key={i} className="flex justify-between text-xs text-slate-600">
                        <span>{a.nombre || a.nif || '(sin nombre)'}{a.nif ? ` · ${a.nif}` : ''}</span>
                        <span>{eur(a.base)} · retenido {eur(a.retencion)}</span>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-slate-500">{resumen.arrendamientos.aviso}</p>
              </CardContent>
            </Card>
          )}
          {resumen && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card><CardContent className="pt-4"><p className="text-xs text-slate-500">Ingresos {ejercicio}</p><p className="text-xl font-bold text-slate-900">{eur(resumen.totales.ingresos)}</p></CardContent></Card>
                <Card><CardContent className="pt-4"><p className="text-xs text-slate-500">Gastos</p><p className="text-xl font-bold text-slate-900">{eur(resumen.totales.gastos)}</p></CardContent></Card>
                <Card><CardContent className="pt-4"><p className="text-xs text-slate-500">Rendimiento neto</p><p className="text-xl font-bold text-indigo-700">{eur(resumen.totales.rendimiento)}</p></CardContent></Card>
                <Card><CardContent className="pt-4"><p className="text-xs text-slate-500">IVA anual</p><p className="text-xl font-bold text-slate-900">{eur(resumen.totales.iva_anual)}</p></CardContent></Card>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader><CardTitle className="text-sm">IVA por trimestre (Modelo 303)</CardTitle></CardHeader>
                  <CardContent className="space-y-1">
                    {resumen.iva_trimestral.map(t => (
                      <div key={t.trimestre} className="flex justify-between text-sm py-1 border-b last:border-0" data-testid={`fiscal-iva-t${t.trimestre}`}>
                        <span>{t.trimestre}T</span>
                        <span className={t.a_ingresar > 0 ? 'text-red-600' : 'text-green-600'}>
                          {t.a_ingresar > 0 ? eur(t.a_ingresar) + ' a ingresar' : (t.a_compensar > 0 ? eur(t.a_compensar) + ' a compensar' : '—')}
                        </span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="text-sm">Pago fraccionado IRPF (Modelo 130)</CardTitle></CardHeader>
                  <CardContent className="space-y-1">
                    {resumen.irpf_trimestral.map(t => (
                      <div key={t.trimestre} className="flex justify-between text-sm py-1 border-b last:border-0" data-testid={`fiscal-irpf-t${t.trimestre}`}>
                        <span>{t.trimestre}T</span>
                        <span className={t.a_ingresar > 0 ? 'text-red-600' : 'text-slate-400'}>
                          {t.a_ingresar > 0 ? eur(t.a_ingresar) + ' a ingresar' : '—'}
                        </span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
              {ret190 && (
                <Card data-testid="fiscal-retenciones-resumen">
                  <CardHeader><CardTitle className="text-sm">Retenciones practicadas por trimestre (Modelo 111)</CardTitle></CardHeader>
                  <CardContent className="space-y-1">
                    {(ret190.trimestral || []).map(t => (
                      <div key={t.trimestre} className="flex justify-between text-sm py-1 border-b last:border-0" data-testid={`fiscal-ret-t${t.trimestre}`}>
                        <span>{t.trimestre}T</span>
                        <span className={Number(t.calculado) > 0 ? 'text-red-600' : 'text-slate-400'}>
                          {Number(t.calculado) > 0 ? `${eur(t.calculado)} a ingresar` : '—'}
                          {t.presentado != null && <span className="text-slate-400"> · presentado {eur(t.presentado)}</span>}
                        </span>
                      </div>
                    ))}
                    <div className="flex justify-between text-sm pt-2 font-semibold">
                      <span>Total {ejercicio} (Modelo 190)</span>
                      <span>{eur(ret190.totales?.retenciones)} · {ret190.totales?.perceptores || 0} perceptor(es)</span>
                    </div>
                    <p className="text-xs text-slate-500 pt-1">
                      Las retenciones de alquiler no se incluyen aquí: corresponden al modelo 115.
                    </p>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* IVA 303 */}
        <TabsContent value="iva" className="mt-4 space-y-4">
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">Trimestre</Label>
            <Select value={String(trimestre)} onValueChange={(v) => setTrimestre(Number(v))}>
              <SelectTrigger className="w-32" data-testid="fiscal-trimestre-iva"><SelectValue /></SelectTrigger>
              <SelectContent>{[1,2,3,4].map(t => <SelectItem key={t} value={String(t)}>{t}º Trimestre</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {iva && (
            <>
              <ResultadoBanner resultado={iva.resultado} aIngresar={iva.a_ingresar} aCompensar={iva.a_compensar} />
              <Card>
                <CardHeader><CardTitle className="text-sm">Modelo 303 · {trimestre}T {ejercicio}</CardTitle></CardHeader>
                <CardContent className="space-y-1">
                  <Casilla codigo="Base" label="Base imponible devengada" valor={iva.casillas.base_devengada} />
                  <Casilla codigo="27" label="IVA devengado (repercutido)" valor={iva.casillas['27_total_devengado']} bold />
                  <Casilla codigo="28-29" label="IVA soportado deducible (op. corrientes)" valor={iva.casillas['28_29_soportado_corriente_cuota']} />
                  <Casilla codigo="30-31" label="IVA soportado bienes de inversión" valor={iva.casillas['30_31_bienes_inversion_cuota']} />
                  <Casilla codigo="45" label="Total a deducir" valor={iva.casillas['45_total_deducir']} bold />
                  <Casilla codigo="46" label="Resultado régimen general" valor={iva.casillas['46_resultado_regimen']} negativo />
                  <Casilla codigo="78" label="Cuotas a compensar de periodos anteriores" valor={iva.casillas['78_cuotas_compensar_previas']} />
                  <Casilla codigo="71" label="Resultado de la liquidación" valor={iva.casillas['71_resultado']} bold negativo />
                </CardContent>
              </Card>
              {iva.por_actividad?.arrendamiento?.facturas > 0 && (
                <Card data-testid="fiscal-303-por-actividad">
                  <CardHeader>
                    <CardTitle className="text-sm">Desglose por actividad (informativo)</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-xs text-slate-500">
                      En IVA hay un único sujeto pasivo y se presenta un solo 303 con las dos
                      actividades. Este desglose es para cuadrarlo con el gestor.
                    </p>
                    {[['profesional', 'Honorarios profesionales'],
                      ['arrendamiento', 'Arrendamiento de inmuebles']].map(([clave, titulo]) => {
                      const bloque = iva.por_actividad?.[clave];
                      if (!bloque) return null;
                      return (
                        <div key={clave} className="border rounded p-3"
                             data-testid={`fiscal-303-actividad-${clave}`}>
                          <p className="text-sm font-medium mb-1">
                            {titulo} <span className="text-xs text-slate-400">
                              · {bloque.facturas} factura(s)</span>
                          </p>
                          <Casilla codigo="Base" label="Base imponible devengada"
                                   valor={bloque.casillas?.base_devengada} />
                          <Casilla codigo="27" label="IVA devengado"
                                   valor={bloque.casillas?.['27_total_devengado']} />
                          <Casilla codigo="45" label="Total a deducir"
                                   valor={bloque.casillas?.['45_total_deducir']} />
                          <Casilla label="Resultado de esta actividad" valor={bloque.resultado}
                                   bold negativo />
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}
              <p className="text-xs text-slate-400 flex items-center gap-1"><Info className="w-3 h-3" /> Facturas incluidas: {iva.drilldown.devengado.length} · Gastos deducidos: {iva.drilldown.soportado.length} · No deducibles: {iva.drilldown.no_deducible.length}</p>
            </>
          )}
        </TabsContent>

        {/* IRPF 130 */}
        <TabsContent value="irpf" className="mt-4 space-y-4">
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">Trimestre</Label>
            <Select value={String(trimestre)} onValueChange={(v) => setTrimestre(Number(v))}>
              <SelectTrigger className="w-32" data-testid="fiscal-trimestre-irpf"><SelectValue /></SelectTrigger>
              <SelectContent>{[1,2,3,4].map(t => <SelectItem key={t} value={String(t)}>{t}º Trimestre</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {irpf && (
            <>
              {irpf.exonerado_130 && (
                <div className="rounded bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="fiscal-exonerado">
                  ⚠️ El {irpf.pct_ingresos_con_retencion}% de tus ingresos llevan retención (≥70%). Podrías estar <b>exonerado</b> de presentar el Modelo 130.
                </div>
              )}
              {irpf.facturas_arrendamiento_excluidas > 0 && (
                <div className="rounded bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900"
                     data-testid="fiscal-130-sin-arrendamientos">
                  {irpf.aviso_arrendamientos}
                </div>
              )}
              <ResultadoBanner resultado={irpf.resultado} aIngresar={irpf.a_ingresar} negativo={irpf.negativo_arrastrar} />
              <Card>
                <CardHeader><CardTitle className="text-sm">Modelo 130 · Acumulado a {trimestre}T {ejercicio}</CardTitle></CardHeader>
                <CardContent className="space-y-1">
                  <Casilla codigo="01" label="Ingresos íntegros" valor={irpf.casillas['01_ingresos']} />
                  <Casilla codigo="02" label="Gastos deducibles (con 5% dif. justificación)" valor={irpf.casillas['02_gastos']} />
                  <Casilla label="  · de los cuales, difícil justificación" valor={irpf.casillas['02_dificil_justificacion']} />
                  <Casilla codigo="03" label="Rendimiento neto" valor={irpf.casillas['03_rendimiento_neto']} bold negativo />
                  <Casilla codigo="04" label="20% sobre rendimiento" valor={irpf.casillas['04_20_por_ciento']} bold />
                  <Casilla codigo="05" label="Pagos fraccionados trimestres anteriores" valor={irpf.casillas['05_pagos_previos']} />
                  <Casilla codigo="06" label="Retenciones soportadas" valor={irpf.casillas['06_retenciones']} />
                  <Casilla codigo="07" label="Resultado" valor={irpf.casillas['07_resultado']} bold negativo />
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* RETENCIONES · MODELO 111 Y RESUMEN ANUAL 190 */}
        <TabsContent value="retenciones" className="mt-4">
          <LiquidacionesTrimestrales ejercicio={ejercicio} solo="111" />
        </TabsContent>

        {/* LIQUIDACIONES TRIMESTRALES (303 + 130 + 111 con detalle y exportación) */}
        <TabsContent value="liquidaciones" className="mt-4">
          <LiquidacionesTrimestrales ejercicio={ejercicio} />
        </TabsContent>

        {/* MODELO 349 · OPERACIONES INTRACOMUNITARIAS */}
        <TabsContent value="intracomunitarias" className="mt-4">
          <Modelo349 ejercicio={ejercicio} />
        </TabsContent>

        {/* JUSTIFICANTES DE LAS PRESENTACIONES (PDF de la AEAT) */}
        <TabsContent value="inmuebles" className="mt-4">
          <InmueblesPanel ejercicio={ejercicio} />
        </TabsContent>

        <TabsContent value="justificantes" className="mt-4">
          <JustificantesFiscales ejercicio={ejercicio} />
        </TabsContent>

        {/* CIERRE ANUAL (390 + libros + export gestoría) */}
        <TabsContent value="cierre" className="mt-4 space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-sm flex items-center gap-2"><FileSpreadsheet className="w-4 h-4" /> Exportación para gestoría</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-slate-500">Genera el libro (ingresos y gastos por código AEAT) en Excel para enviar a tu gestoría.</p>
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <Label className="text-xs">Periodo</Label>
                  <Select value={exportTrim} onValueChange={setExportTrim}>
                    <SelectTrigger className="w-40" data-testid="export-periodo"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Anual</SelectItem>
                      <SelectItem value="1">1º Trimestre</SelectItem>
                      <SelectItem value="2">2º Trimestre</SelectItem>
                      <SelectItem value="3">3º Trimestre</SelectItem>
                      <SelectItem value="4">4º Trimestre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Formato</Label>
                  <Select value={exportFormato} onValueChange={setExportFormato}>
                    <SelectTrigger className="w-56" data-testid="export-formato"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="completo">Completo (varias hojas)</SelectItem>
                      <SelectItem value="aeat">Resumen por código AEAT</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={descargarGestoria} disabled={exporting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="export-gestoria-btn">
                  {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}Descargar Excel
                </Button>
                <Button
                  onClick={descargarGestoriaZip}
                  disabled={exportingZip}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="export-gestoria-zip-btn"
                >
                  {exportingZip ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  ZIP completo (Excel + facturas)
                </Button>
                <Button onClick={descargarLibrosAEAT} variant="outline" data-testid="export-libros-aeat-btn">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />Libros Registro AEAT
                </Button>
              </div>
            </CardContent>
          </Card>

          {m390 && (
            <Card>
              <CardHeader><CardTitle className="text-sm">Modelo 390 · Resumen anual de IVA {ejercicio}</CardTitle></CardHeader>
              <CardContent className="space-y-1">
                <Casilla label="IVA devengado (repercutido) total" valor={m390.casillas.iva_devengado_total} bold />
                <Casilla label="IVA soportado deducible total" valor={m390.casillas.iva_soportado_deducible_total} />
                <Casilla label="Bienes de inversión (cuota soportada)" valor={m390.casillas.bienes_inversion_cuota_total} />
                <Casilla label="Resultado anual de liquidaciones" valor={m390.casillas.resultado_liquidaciones_anual} bold negativo />
                <Casilla label="Total ingresado en el ejercicio" valor={m390.casillas.total_ingresado_ejercicio} />
              </CardContent>
            </Card>
          )}

          {m100 && (
            <Card data-testid="fiscal-modelo100">
              <CardHeader><CardTitle className="text-sm">Borrador Renta · Rendimiento y cuota estimada {ejercicio}</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="rounded-lg border bg-slate-50/60 p-3 grid grid-cols-2 md:grid-cols-5 gap-3 items-end" data-testid="renta-datos-personales">
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={dp.edad65} onChange={e => setDp({ ...dp, edad65: e.target.checked, edad75: e.target.checked ? dp.edad75 : false })} data-testid="renta-edad65" /> ≥65 años</label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={dp.edad75} onChange={e => setDp({ ...dp, edad75: e.target.checked, edad65: e.target.checked ? true : dp.edad65 })} /> ≥75 años</label>
                  <div><Label className="text-xs">Nº hijos</Label><Input type="number" min="0" value={dp.hijos} onChange={e => setDp({ ...dp, hijos: parseInt(e.target.value) || 0 })} data-testid="renta-hijos" /></div>
                  <div><Label className="text-xs">Hijos &lt;3 años</Label><Input type="number" min="0" value={dp.hijos_menor3} onChange={e => setDp({ ...dp, hijos_menor3: parseInt(e.target.value) || 0 })} /></div>
                  <div>
                    <Label className="text-xs">Discapacidad</Label>
                    <Select value={String(dp.discapacidad)} onValueChange={v => setDp({ ...dp, discapacidad: parseInt(v) })}>
                      <SelectTrigger data-testid="renta-discapacidad"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">No</SelectItem>
                        <SelectItem value="33">≥33%</SelectItem>
                        <SelectItem value="65">≥65%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Casilla label="Rendimiento neto de la actividad" valor={m100.casillas.rendimiento_neto} bold negativo />
                <Casilla label="Mínimo personal y familiar" valor={m100.casillas.minimo_personal_familiar} />
                <Casilla label="Cuota íntegra estimada" valor={m100.casillas.cuota_integra_estimada} bold />
                <div className="flex justify-between text-sm py-1 border-b"><span>Tipo medio estimado</span><span>{(m100.casillas.tipo_medio_estimado || 0).toFixed(2)}%</span></div>
                <Casilla label="Retenciones + pagos fraccionados" valor={m100.casillas.total_a_cuenta} />
                <Casilla label="Resultado estimado (+ a pagar / − a devolver)" valor={m100.casillas.resultado_estimado} bold negativo />
                <p className="text-xs text-slate-400 flex items-center gap-1 pt-2"><Info className="w-3 h-3" /> {m100.nota}</p>
              </CardContent>
            </Card>
          )}

          {m100?.modelo_347 && (m100.modelo_347.clientes.length > 0 || m100.modelo_347.proveedores.length > 0) && (
            <Card data-testid="fiscal-modelo347">
              <CardHeader><CardTitle className="text-sm">Aviso Modelo 347 · Operaciones con terceros &gt; {eur(m100.modelo_347.umbral)}</CardTitle></CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-1">Clientes</p>
                  {m100.modelo_347.clientes.length ? m100.modelo_347.clientes.map((c, i) => (
                    <div key={i} className="flex justify-between text-sm py-1 border-b last:border-0"><span>{c.nombre}</span><span>{eur(c.total)}</span></div>
                  )) : <p className="text-xs text-slate-400">Ninguno supera el umbral</p>}
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-1">Proveedores</p>
                  {m100.modelo_347.proveedores.length ? m100.modelo_347.proveedores.map((p, i) => (
                    <div key={i} className="flex justify-between text-sm py-1 border-b last:border-0"><span>{p.nombre}</span><span>{eur(p.total)}</span></div>
                  )) : <p className="text-xs text-slate-400">Ninguno supera el umbral</p>}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* BIENES DE INVERSIÓN */}
        <TabsContent value="bienes" className="mt-4">
          <BienesInversion ejercicio={ejercicio} />
        </TabsContent>

        {/* CONFIG */}
        <TabsContent value="config" className="mt-4">
          {config && (
            <Card>
              <CardHeader><CardTitle className="text-sm">Configuración fiscal del titular</CardTitle></CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-4">
                <div><Label>Titular</Label><Input value={config.titular_nombre || ''} onChange={e => setConfig({ ...config, titular_nombre: e.target.value })} data-testid="fiscal-cfg-titular" /></div>
                <div><Label>NIF/CIF</Label><Input value={config.titular_nif || ''} onChange={e => setConfig({ ...config, titular_nif: e.target.value })} /></div>
                <div>
                  <Label>Tipo de persona</Label>
                  <Select value={config.tipo_persona} onValueChange={v => setConfig({ ...config, tipo_persona: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fisica">Persona física (autónomo)</SelectItem>
                      <SelectItem value="juridica">Persona jurídica (SLP/empresa)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Epígrafe IAE</Label><Input value={config.epigrafe_iae || ''} onChange={e => setConfig({ ...config, epigrafe_iae: e.target.value })} /></div>
                <div>
                  <Label>Régimen IRPF</Label>
                  <Select value={config.regimen_irpf} onValueChange={v => setConfig({ ...config, regimen_irpf: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="eds">Estimación Directa Simplificada</SelectItem>
                      <SelectItem value="ed_normal">Estimación Directa Normal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>% Retención IRPF por defecto</Label><Input type="number" value={config.porcentaje_retencion_default} onChange={e => setConfig({ ...config, porcentaje_retencion_default: Number(e.target.value) })} /></div>
                <div><Label>% IVA por defecto</Label><Input type="number" value={config.porcentaje_iva_default} onChange={e => setConfig({ ...config, porcentaje_iva_default: Number(e.target.value) })} /></div>
                <div><Label>% Gastos difícil justificación (EDS, máx 2.000€)</Label><Input type="number" value={config.porcentaje_gastos_dificil_justificacion} onChange={e => setConfig({ ...config, porcentaje_gastos_dificil_justificacion: Number(e.target.value) })} /></div>
                <div><Label>% Prorrata IVA</Label><Input type="number" value={config.prorrata_iva} onChange={e => setConfig({ ...config, prorrata_iva: Number(e.target.value) })} /></div>
                <div className="md:col-span-2">
                  <Button onClick={saveConfig} disabled={savingCfg} className="bg-indigo-600 hover:bg-indigo-700" data-testid="fiscal-cfg-guardar">
                    {savingCfg ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Guardar configuración
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FiscalidadTab;
